import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Activity, Loader2, Zap, AlertTriangle, CheckCircle, Lightbulb,
  TrendingUp, TrendingDown, Target, BarChart3, Shuffle, Clock, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScenarioSimulatorView = ({ contracts }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);
  const [whatIfTrigger, setWhatIfTrigger] = useState("");
  const [riskTemplates, setRiskTemplates] = useState([]);
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [form, setForm] = useState({
    project_name: "", sector: "Infrastructure", framework: "FIDIC", delivery_model: "EPC",
    contract_value: "", duration_months: "", current_progress_percent: 0, current_spend_percent: 0
  });

  React.useEffect(() => {
    axios.get(`${API}/scenarios/risk-templates`).then(r => setRiskTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const toggleRisk = (tmpl) => {
    setSelectedRisks(prev => prev.find(r => r.id === tmpl.id) ? prev.filter(r => r.id !== tmpl.id) : [...prev, tmpl]);
  };

  const runSimulation = async () => {
    if (!form.project_name || !form.contract_value) { toast.error("Fill in project name and value"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        contract_value: parseFloat(form.contract_value),
        duration_months: parseInt(form.duration_months) || 12,
        current_progress_percent: parseFloat(form.current_progress_percent) || 0,
        current_spend_percent: parseFloat(form.current_spend_percent) || 0,
        risk_events: selectedRisks.map(r => ({ name: r.name, category: r.category, ...r.default_impact })),
        what_if_triggers: []
      };
      const res = await axios.post(`${API}/scenarios/simulate`, payload);
      setResult(res.data.simulation);
      setScenarioId(res.data.scenario_id);
      setWhatIfResult(null);
      toast.success("Scenario simulation complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation failed"); }
    finally { setLoading(false); }
  };

  const runWhatIf = async () => {
    if (!scenarioId || !whatIfTrigger) { toast.error("Run a simulation first and enter a what-if trigger"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scenarios/what-if`, { scenario_id: scenarioId, trigger: whatIfTrigger });
      setWhatIfResult(res.data.what_if_result);
      toast.success("What-if analysis complete");
    } catch (e) { toast.error(e.response?.data?.detail || "What-if failed"); }
    finally { setLoading(false); }
  };

  const fmt = (v) => v ? `$${(v / 1000000).toFixed(1)}M` : "$0";
  const ScenarioCard = ({ title, data, color, icon: Icon }) => (
    <div className={`border-2 ${color} bg-white p-5 space-y-3`}>
      <div className="flex items-center gap-2"><Icon size={18} /><h4 className="font-bold text-sm uppercase">{title}</h4></div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-xs text-zinc-500">Cost</p><p className="font-black text-lg">{fmt(data?.total_cost)}</p></div>
        <div><p className="text-xs text-zinc-500">Duration</p><p className="font-black text-lg">{data?.duration_months || 0}m</p></div>
        <div><p className="text-xs text-zinc-500">Cost Variance</p><p className={`font-bold ${(data?.cost_variance_percent || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{data?.cost_variance_percent > 0 ? '+' : ''}{data?.cost_variance_percent?.toFixed(1) || 0}%</p></div>
        <div><p className="text-xs text-zinc-500">Schedule Offset</p><p className={`font-bold ${(data?.completion_date_offset_days || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{data?.completion_date_offset_days > 0 ? '+' : ''}{data?.completion_date_offset_days || 0}d</p></div>
        <div><p className="text-xs text-zinc-500">Risk Score</p><p className={`font-bold ${(data?.risk_score || 0) > 60 ? 'text-red-600' : 'text-emerald-600'}`}>{data?.risk_score || 0}/100</p></div>
        <div><p className="text-xs text-zinc-500">Probability</p><p className="font-bold">{((data?.probability || 0) * 100).toFixed(0)}%</p></div>
      </div>
      {data?.key_assumptions && (
        <div className="pt-2 border-t border-zinc-200">
          {data.key_assumptions.slice(0, 3).map((a, i) => (
            <p key={i} className="text-xs text-zinc-500 flex items-start gap-1 mt-1"><CheckCircle size={10} className="text-zinc-400 mt-0.5 flex-shrink-0" />{a}</p>
          ))}
        </div>
      )}
      <p className="text-xs text-zinc-600">{data?.description}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="scenario-simulator-view">
      <div>
        <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Scenario Simulator</h1>
        <p className="text-zinc-500 mt-1">Forecast project outcomes under different risk scenarios</p>
      </div>

      <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-4">
        <h2 className="text-lg font-bold font-['Chivo']">Project Parameters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Project Name</label>
            <input value={form.project_name} onChange={e => setForm(p => ({ ...p, project_name: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="Project name" data-testid="sim-project-name" /></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Sector</label>
            <select value={form.sector} onChange={e => setForm(p => ({ ...p, sector: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm">
              {["Infrastructure","Energy","Buildings","Industrial","Water","Mining"].map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract Value ($)</label>
            <input type="number" value={form.contract_value} onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="50000000" data-testid="sim-value" /></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Duration (months)</label>
            <input type="number" value={form.duration_months} onChange={e => setForm(p => ({ ...p, duration_months: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="24" data-testid="sim-duration" /></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework</label>
            <select value={form.framework} onChange={e => setForm(p => ({ ...p, framework: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm">
              {["FIDIC","NEC","JCT","ICC","AIA","EJCDC"].map(f => <option key={f}>{f}</option>)}</select></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Delivery Model</label>
            <select value={form.delivery_model} onChange={e => setForm(p => ({ ...p, delivery_model: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm">
              {["EPC","Design & Build","Traditional","EPCM","Alliance","PPP","BOT"].map(d => <option key={d}>{d}</option>)}</select></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Progress (%)</label>
            <input type="number" value={form.current_progress_percent} onChange={e => setForm(p => ({ ...p, current_progress_percent: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="0" /></div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Spend (%)</label>
            <input type="number" value={form.current_spend_percent} onChange={e => setForm(p => ({ ...p, current_spend_percent: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="0" /></div>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase text-zinc-500 mb-2">Risk Events to Simulate ({selectedRisks.length} selected)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {riskTemplates.map((tmpl, i) => {
              const isSelected = selectedRisks.find(r => r.id === tmpl.id);
              return (
                <div key={i} className={`p-2 border-2 cursor-pointer text-xs ${isSelected ? 'border-[#E11D48] bg-orange-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                  onClick={() => toggleRisk(tmpl)} data-testid={`risk-${tmpl.id}`}>
                  <p className="font-medium">{tmpl.name}</p>
                  <p className="text-zinc-500">{tmpl.category} | +{tmpl.default_impact.cost_percent}% cost | +{tmpl.default_impact.schedule_days}d</p>
                </div>
              );
            })}
          </div>
        </div>

        <Button onClick={runSimulation} disabled={loading || !form.project_name} className="bg-black text-white px-8" data-testid="btn-run-simulation">
          {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Simulating...</> : <><Activity size={16} className="mr-2" /> Run Simulation</>}
        </Button>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ScenarioCard title="Optimistic" data={result.optimistic} color="border-emerald-400" icon={TrendingUp} />
            <ScenarioCard title="Most Likely" data={result.most_likely} color="border-[#EAB308]" icon={Target} />
            <ScenarioCard title="Pessimistic" data={result.pessimistic} color="border-red-400" icon={TrendingDown} />
          </div>

          {result.contingency_recommendation && (
            <div className="border-2 border-amber-300 bg-amber-50 p-4">
              <h3 className="text-xs font-bold uppercase text-amber-600 mb-2">Contingency Recommendation</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><p className="text-xs text-zinc-500">Cost Contingency</p><p className="font-bold text-lg">{result.contingency_recommendation.cost_contingency_percent}%</p></div>
                <div><p className="text-xs text-zinc-500">Schedule Buffer</p><p className="font-bold text-lg">{result.contingency_recommendation.schedule_contingency_days}d</p></div>
                <div><p className="text-xs text-zinc-500">Management Reserve</p><p className="font-bold text-lg">{result.contingency_recommendation.management_reserve_percent}%</p></div>
              </div>
            </div>
          )}

          {result.risk_impacts && (
            <div className="border-2 border-black brutal-shadow bg-white p-6">
              <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Risk Impact Analysis</h3>
              <div className="space-y-2">
                {result.risk_impacts.map((r, i) => (
                  <div key={i} className="p-3 border border-zinc-200 flex items-center gap-4">
                    <AlertTriangle size={16} className={r.probability > 0.5 ? "text-red-500" : "text-amber-500"} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{r.event}</p>
                      <p className="text-xs text-zinc-500">{r.mitigation}</p>
                    </div>
                    <div className="text-right text-xs">
                      <p>P: {(r.probability * 100).toFixed(0)}%</p>
                      <p className="text-red-600">+{fmt(r.cost_impact)} | +{r.schedule_impact_days}d</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.critical_decision_points && (
            <div className="border-2 border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Critical Decision Points</h3>
              <div className="space-y-2">
                {result.critical_decision_points.map((d, i) => (
                  <div key={i} className="p-3 border-l-4 border-[#EAB308] bg-blue-50">
                    <p className="text-sm font-medium">{d.decision}</p>
                    <p className="text-xs text-zinc-500">Timing: {d.timing} | Recommended: {d.recommended_action}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-3">
            <h3 className="text-sm font-bold font-['Chivo']">What-If Analysis</h3>
            <div className="flex gap-3">
              <input value={whatIfTrigger} onChange={e => setWhatIfTrigger(e.target.value)} placeholder="e.g., 'Main steel supplier goes bankrupt' or '3 months of heavy monsoon rain'" className="flex-1 border-2 border-zinc-200 px-4 py-2 text-sm" data-testid="what-if-input" />
              <Button onClick={runWhatIf} disabled={loading} className="bg-[#E11D48] text-white" data-testid="btn-what-if">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Shuffle size={14} className="mr-1" />} Analyze
              </Button>
            </div>

            {whatIfResult && (
              <div className="mt-4 space-y-3 pt-4 border-t border-zinc-200">
                <h4 className="text-xs font-bold uppercase text-zinc-500">Impact: "{whatIfResult.trigger}"</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-zinc-50 border text-center"><p className="text-xs text-zinc-500">Cost Impact</p><p className="font-bold text-red-600">+{whatIfResult.impact_assessment?.cost_impact_percent?.toFixed(1)}%</p></div>
                  <div className="p-3 bg-zinc-50 border text-center"><p className="text-xs text-zinc-500">Schedule Impact</p><p className="font-bold text-red-600">+{whatIfResult.impact_assessment?.schedule_impact_days}d</p></div>
                  <div className="p-3 bg-zinc-50 border text-center"><p className="text-xs text-zinc-500">Quality</p><p className="font-bold">{whatIfResult.impact_assessment?.quality_impact}</p></div>
                  <div className="p-3 bg-zinc-50 border text-center"><p className="text-xs text-zinc-500">Safety</p><p className="font-bold">{whatIfResult.impact_assessment?.safety_impact}</p></div>
                </div>
                {whatIfResult.mitigation_options && (
                  <div>{whatIfResult.mitigation_options.map((m, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm mt-1"><Lightbulb size={12} className="text-amber-500" /><span>{m.option}</span><Badge variant="outline" className="text-xs ml-auto">{m.effectiveness}</Badge></div>
                  ))}</div>
                )}
                {whatIfResult.recommendation && <div className="p-3 bg-blue-50 border border-blue-200 text-sm">{whatIfResult.recommendation}</div>}
              </div>
            )}
          </div>

          {result.executive_summary && (
            <div className="p-4 bg-zinc-900 text-white"><p className="text-sm">{result.executive_summary}</p></div>
          )}
        </>
      )}
    </div>
  );
};

export default ScenarioSimulatorView;
